/**
 * WizardPage — 3-step proposal wizard
 *
 * Step 1: Site & Tariff — address, geocode, tariff selection
 * Step 2: Load — upload CSV or pick from library
 * Step 3: Results — submit optimizations, show tier comparison, generate proposal
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stepper, Step, StepLabel, Button, Typography, Paper, Alert,
} from '@mui/material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import SiteStep from '../components/SiteStep';
import LoadStep from '../components/LoadStep';
import ResultsStep from '../components/ResultsStep';
import { useAutoProposal } from '../hooks/useAutoProposal';

const STEPS = ['Site & Tariff', 'Load Profile', 'Results'];

function WizardPage() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  // Wizard state
  const [siteData, setSiteData] = useState(null);   // { address, lat, lon, tariff_data?, customer_name? }
  const [loadData, setLoadData] = useState(null);    // { loads_kw, source_file?, curve_id? }

  const proposal = useAutoProposal();

  const handleSiteComplete = useCallback((data) => {
    setSiteData(data);
    setActiveStep(1);
  }, []);

  const handleLoadComplete = useCallback((data) => {
    setLoadData(data);
    setActiveStep(2);

    // Auto-start the proposal flow
    proposal.startProposal({
      address: siteData.address,
      lat: siteData.lat,
      lon: siteData.lon,
      loads_kw: data.loads_kw,
      tariff_data: siteData.tariff_data,
      customer_name: siteData.customer_name,
      curve_id: data.curve_id,
    });
  }, [siteData, proposal]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  }, []);

  const handleStartOver = useCallback(() => {
    setSiteData(null);
    setLoadData(null);
    setActiveStep(0);
    proposal.reset();
  }, [proposal]);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 4, px: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: vortxTypography.heading,
            fontWeight: 700,
            color: vortxColors.charcoal,
            mb: 1,
          }}
        >
          New Proposal
        </Typography>
        <Typography
          sx={{
            fontFamily: vortxTypography.body,
            color: vortxColors.charcoal,
            opacity: 0.7,
          }}
        >
          Generate a battery storage proposal in 3 steps
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontFamily: vortxTypography.body,
                  fontWeight: 600,
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          border: `1px solid ${vortxColors.charcoal}15`,
        }}
      >
        {activeStep === 0 && (
          <SiteStep
            initialData={siteData}
            onComplete={handleSiteComplete}
          />
        )}

        {activeStep === 1 && (
          <LoadStep
            initialData={loadData}
            onComplete={handleLoadComplete}
            onBack={handleBack}
          />
        )}

        {activeStep === 2 && (
          <ResultsStep
            proposal={proposal}
            siteData={siteData}
            loadData={loadData}
            onStartOver={handleStartOver}
          />
        )}
      </Paper>
    </Box>
  );
}

export default WizardPage;
